import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState(location.state?.email || "");
  const fromJoin = location.state?.fromJoin;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email, code });
      setUser(data);
      toast.success("Email verified");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/verify-email/resend", { email });
      toast.success("A new code has been sent (if the email exists)");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const requestApproval = async () => {
    try {
      const { data } = await api.post("/join/request-approval", { email });
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <AuthShell title="Verify your email" subtitle="Enter the 6-digit code we emailed you">
      <form onSubmit={verify} className="space-y-4" data-testid="verify-form">
        <div>
          <Label htmlFor="ve">Email</Label>
          <Input id="ve" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5" data-testid="verify-email" />
        </div>
        <div>
          <Label htmlFor="vc">Verification code</Label>
          <Input id="vc" required value={code} onChange={(e) => setCode(e.target.value)} maxLength={6}
            className="mt-1.5 tracking-[0.4em] text-center text-lg" placeholder="000000" data-testid="verify-code" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="verify-submit">
          {loading ? "Verifying…" : "Verify email"}
        </Button>
        <div className="flex items-center justify-between text-sm">
          <button type="button" onClick={resend} className="text-indigo-600 hover:underline" data-testid="verify-resend">
            Resend code
          </button>
          {fromJoin && (
            <button type="button" onClick={requestApproval} className="text-slate-500 hover:underline" data-testid="verify-request-approval">
              Code not arriving? Request admin approval
            </button>
          )}
        </div>
      </form>
    </AuthShell>
  );
}
